import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { Send, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tables } from "@/lib/supabase/supabaseTypes";
import { ExerciseCollection, ExerciseSet } from "@/components/workouts/workoutsColumns";
import { streamWorkoutBuilder } from "@/lib/ai/streamWorkoutBuilder";
import type { AiChatMessage, AiExerciseSet, AiToolCall } from "@/lib/ai/types";
import AiChatMessageComponent, { StreamingBubble } from "./AiChatMessage";

const STARTER_SUGGESTIONS = [
  "Build a lower body workout without squats",
  "Upper body push day, 3-4 sets of 8-10 reps",
  "Full body workout, 3 exercises per muscle group",
  "Add more hamstring-focused exercises",
];

export interface AiExerciseConfig {
  sets: ExerciseSet[];
  tempo?: string;
  rir?: number;
  rest_time?: number;
  coach_note?: string;
}

export interface AiUpdateConfig {
  sets?: ExerciseSet[];
  tempo?: string;
  rir?: number;
  rest_time?: number;
  coach_note?: string;
}

interface AiWorkoutChatProps {
  exerciseDatabase: Tables<"exercises">[] | undefined;
  onAddExercise: (exercise: Tables<"exercises">, config: AiExerciseConfig) => void;
  onRemoveExercise: (exOrder: number) => void;
  onReplaceExercise: (
    exOrder: number,
    exercise: Tables<"exercises">,
    config: AiExerciseConfig,
  ) => void;
  onUpdateExercise: (exOrder: number, config: AiUpdateConfig) => void;
  currentExerciseCollection: ExerciseCollection[];
}

const AiWorkoutChat: FunctionComponent<AiWorkoutChatProps> = ({
  exerciseDatabase,
  onAddExercise,
  onRemoveExercise,
  onReplaceExercise,
  onUpdateExercise,
  currentExerciseCollection,
}) => {
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const scrollEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Ref so the streaming onEvent closure can always read the latest collection
  // without needing currentExerciseCollection in sendMessage's deps.
  const exerciseCollectionRef = useRef(currentExerciseCollection);
  exerciseCollectionRef.current = currentExerciseCollection;

  // Abort any in-flight stream when the sidebar closes / component unmounts
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Build a lookup map of exercise ID -> name for display in tool call chips
  const exerciseNameMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const ex of exerciseDatabase ?? []) {
      map[ex.id] = ex.name;
    }
    return map;
  }, [exerciseDatabase]);

  // Build the current workout summary sent as context to the AI.
  // Including sets lets the AI preserve existing values in update_exercise calls.
  const currentWorkoutSummary = useMemo(
    () =>
      currentExerciseCollection.flatMap((group, exOrder) =>
        group.map((ex) => ({
          ex_order: exOrder,
          name: ex.name,
          tracking_type: ex.tracking_type ?? "strength",
          sets: (ex.sets ?? []).map((s) => ({
            startRange: s.startRange,
            endRange: s.endRange,
            targetTimeSeconds: s.targetTimeSeconds,
            targetDistanceMeters: s.targetDistanceMeters,
          })),
        })),
      ),
    [currentExerciseCollection],
  );

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      scrollEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingText, scrollToBottom]);

  const handleToolCall = useCallback(
    (call: AiToolCall) => {
      const buildSets = (rawSets: AiExerciseSet[]): ExerciseSet[] =>
        (rawSets ?? []).map((s) => ({
          id: uuidv4(),
          startRange: s.startRange,
          endRange: s.endRange,
          targetWeight: s.targetWeight,
          targetTimeSeconds: s.targetTimeSeconds,
          targetDistanceMeters: s.targetDistanceMeters,
        }));

      if (call.name === "add_exercise") {
        const exercise = (exerciseDatabase ?? []).find(
          (ex) => ex.id === call.args.exercise_id,
        );
        if (!exercise) return;
        onAddExercise(exercise, {
          sets: buildSets(call.args.sets),
          tempo: call.args.tempo,
          rir: call.args.rir,
          rest_time: call.args.rest_time,
          coach_note: call.args.coach_note,
        });
      } else if (call.name === "remove_exercise") {
        onRemoveExercise(call.args.ex_order);
      } else if (call.name === "replace_exercise") {
        const exercise = (exerciseDatabase ?? []).find(
          (ex) => ex.id === call.args.exercise_id,
        );
        if (!exercise) return;
        onReplaceExercise(call.args.ex_order, exercise, {
          sets: buildSets(call.args.sets),
          tempo: call.args.tempo,
          rir: call.args.rir,
          rest_time: call.args.rest_time,
          coach_note: call.args.coach_note,
        });
      } else if (call.name === "update_exercise") {
        onUpdateExercise(call.args.ex_order, {
          sets: call.args.sets ? buildSets(call.args.sets) : undefined,
          tempo: call.args.tempo,
          rir: call.args.rir,
          rest_time: call.args.rest_time,
          coach_note: call.args.coach_note,
        });
      }
    },
    [exerciseDatabase, onAddExercise, onRemoveExercise, onReplaceExercise, onUpdateExercise],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;

      const userMessage: AiChatMessage = { role: "user", content: trimmed };
      const updatedMessages = [...messages, userMessage];

      setMessages(updatedMessages);
      setInput("");
      setIsStreaming(true);
      setStreamingText("");

      const collectedToolCalls: AiToolCall[] = [];
      let accumulatedText = "";

      // Track which AI-provided positions have been removed so subsequent
      // ex_order values (which the AI based on the original snapshot) can be
      // adjusted to reflect the shifted collection.
      const removedOriginalPositions: number[] = [];
      const adjustExOrder = (aiOrder: number): number =>
        aiOrder - removedOriginalPositions.filter((r) => r < aiOrder).length;

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      await streamWorkoutBuilder({
        messages: updatedMessages,
        currentWorkout: currentWorkoutSummary,
        signal: controller.signal,
        onEvent: (event) => {
          if (event.type === "text_delta") {
            accumulatedText += event.content;
            // Never show streaming text in the bubble — always keep the thinking
            // indicator going. Text only appears in the final committed message.
          } else if (event.type === "tool_call_complete") {
            // Enrich calls with exercise names captured NOW (before handleToolCall
            // mutates state), and adjust ex_order to account for any prior removals
            // in this same response.
            let call = event.call;
            const col = exerciseCollectionRef.current;
            if (call.name === "remove_exercise") {
              const originalOrder = call.args.ex_order;
              const adjustedOrder = adjustExOrder(originalOrder);
              const exName = col[adjustedOrder]?.[0]?.name;
              call = { ...call, args: { ...call.args, ex_order: adjustedOrder, ...(exName ? { _exerciseName: exName } : {}) } };
              removedOriginalPositions.push(originalOrder);
            } else if (call.name === "replace_exercise") {
              const adjustedOrder = adjustExOrder(call.args.ex_order);
              const oldName = col[adjustedOrder]?.[0]?.name;
              call = { ...call, args: { ...call.args, ex_order: adjustedOrder, ...(oldName ? { _oldName: oldName } : {}) } };
            } else if (call.name === "update_exercise") {
              const adjustedOrder = adjustExOrder(call.args.ex_order);
              const exName = col[adjustedOrder]?.[0]?.name;
              call = { ...call, args: { ...call.args, ex_order: adjustedOrder, ...(exName ? { _exerciseName: exName } : {}) } };
            }
            collectedToolCalls.push(call);
            handleToolCall(call);
          } else if (event.type === "done") {
            const assistantMsg: AiChatMessage = {
              role: "assistant",
              content: accumulatedText,
              toolCalls: collectedToolCalls.length > 0 ? collectedToolCalls : undefined,
            };
            if (assistantMsg.content || assistantMsg.toolCalls) {
              setMessages((prev) => [...prev, assistantMsg]);
            } else {
              setMessages((prev) => [
                ...prev,
                { role: "assistant", content: "Done." },
              ]);
            }
            setStreamingText("");
            setIsStreaming(false);
          } else if (event.type === "error") {
            const assistantMessage: AiChatMessage = {
              role: "assistant",
              content: accumulatedText || "",
              toolCalls:
                collectedToolCalls.length > 0 ? collectedToolCalls : undefined,
            };
            // Only add the partial message if something was received
            if (assistantMessage.content || assistantMessage.toolCalls) {
              setMessages((prev) => [...prev, assistantMessage]);
            }
            setMessages((prev) => [
              ...prev,
              {
                role: "assistant",
                content: `Something went wrong: ${event.message}. Please try again.`,
              },
            ]);
            setStreamingText("");
            setIsStreaming(false);
          }
        },
      });
    },
    [messages, isStreaming, currentWorkoutSummary, handleToolCall],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const isEmpty = messages.length === 0 && !isStreaming;

  return (
    <Card className="sticky max-w-[400px] min-w-[350px] h-[80vh] top-6 flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b flex-shrink-0">
        <Sparkles className="size-4 text-primary" />
        <span className="font-semibold text-sm">AI Builder</span>
      </div>

      {/* Messages */}
      <CardContent className="flex flex-col flex-1 overflow-hidden p-0">
        <ScrollArea className="flex-1 px-3 py-3">
          {isEmpty ? (
            <EmptyState
              suggestions={STARTER_SUGGESTIONS}
              onSuggestionClick={(s) => {
                setInput(s);
                textareaRef.current?.focus();
              }}
            />
          ) : (
            <div className="flex flex-col gap-3">
              {messages.map((msg, i) => (
                <AiChatMessageComponent
                  key={i}
                  message={msg}
                  exerciseNames={exerciseNameMap}
                />
              ))}
              {isStreaming && <StreamingBubble text={streamingText} />}
              <div ref={scrollEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input */}
        <div className="border-t p-3 flex-shrink-0">
          <div className="flex gap-2 items-end">
            <Textarea
              ref={textareaRef}
              placeholder="Describe a workout..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
              rows={2}
              className="resize-none text-sm min-h-[60px] max-h-[120px]"
            />
            <Button
              size="icon"
              disabled={!input.trim() || isStreaming}
              onClick={() => sendMessage(input)}
              className="flex-shrink-0 mb-0.5"
            >
              <Send className="size-4" />
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5 text-center">
            Enter to send · Shift+Enter for new line
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

interface EmptyStateProps {
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
}

const EmptyState: FunctionComponent<EmptyStateProps> = ({
  suggestions,
  onSuggestionClick,
}) => (
  <div className="flex flex-col items-center gap-4 py-6 px-2">
    <div className="flex flex-col items-center gap-2 text-center">
      <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center">
        <Sparkles className="size-5 text-primary" />
      </div>
      <p className="text-sm font-medium">AI Workout Builder</p>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Describe a workout and I'll build it using your exercise library. You
        can refine it with follow-up messages.
      </p>
    </div>
    <div className="flex flex-col gap-2 w-full">
      {suggestions.map((s) => (
        <button
          key={s}
          onClick={() => onSuggestionClick(s)}
          className="text-left text-xs px-3 py-2 rounded-lg border border-border hover:bg-accent hover:border-primary/30 transition-colors text-muted-foreground hover:text-foreground"
        >
          {s}
        </button>
      ))}
    </div>
  </div>
);

export default AiWorkoutChat;
